import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc, getDoc, updateDoc, arrayUnion, arrayRemove, increment,
  collection, query, where, orderBy, getDocs, addDoc, serverTimestamp, runTransaction
} from "firebase/firestore";
import { db } from "../firebase";

function PostDetail({ user }) {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentInput, setCommentInput] = useState("");
  const myNickname = user?.nickname;

  // 게시글 불러오기
  useEffect(() => {
    const fetchPost = async () => {
      const docRef = doc(db, "posts", postId);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        setPost({ id: snap.id, ...snap.data() });
        await updateDoc(docRef, { views: increment(1) });
      }
    };
    fetchPost();
  }, [postId]);

  // 댓글 불러오기
  useEffect(() => {
    const fetchComments = async () => {
      const q = query(
        collection(db, "comments"),
        where("postId", "==", postId),
        orderBy("createdAt", "asc")
      );
      const snap = await getDocs(q);
      setComments(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchComments();
  }, [postId]);

  // 추천 트랜잭션
  const handleLike = async () => {
    if (!myNickname) return alert("로그인 필요");
    const postRef = doc(db, "posts", postId);

    try {
      await runTransaction(db, async (transaction) => {
        const postSnap = await transaction.get(postRef);
        if (!postSnap.exists()) throw new Error("게시글 없음");
        const data = postSnap.data();

        const liked = data.likedBy?.includes(myNickname);
        const disliked = data.dislikedBy?.includes(myNickname);
        const update = {};

        if (liked) {
          update.likes = (data.likes || 0) - 1;
          update.likedBy = arrayRemove(myNickname);
        } else {
          update.likes = (data.likes || 0) + 1;
          update.likedBy = arrayUnion(myNickname);
          if (disliked) {
            update.dislikes = (data.dislikes || 0) - 1;
            update.dislikedBy = arrayRemove(myNickname);
          }
        }

        transaction.update(postRef, update);
      });

      const snap = await getDoc(postRef);
      setPost({ id: snap.id, ...snap.data() });
    } catch (e) {
      console.error("추천 처리 오류:", e);
    }
  };

  // 비추천 트랜잭션
  const handleDislike = async () => {
    if (!myNickname) return alert("로그인 필요");
    const postRef = doc(db, "posts", postId);

    try {
      await runTransaction(db, async (transaction) => {
        const postSnap = await transaction.get(postRef);
        if (!postSnap.exists()) throw new Error("게시글 없음");
        const data = postSnap.data();

        const disliked = data.dislikedBy?.includes(myNickname);
        const liked = data.likedBy?.includes(myNickname);
        const update = {};

        if (disliked) {
          update.dislikes = (data.dislikes || 0) - 1;
          update.dislikedBy = arrayRemove(myNickname);
        } else {
          update.dislikes = (data.dislikes || 0) + 1;
          update.dislikedBy = arrayUnion(myNickname);
          if (liked) {
            update.likes = (data.likes || 0) - 1;
            update.likedBy = arrayRemove(myNickname);
          }
        }

        transaction.update(postRef, update);
      });

      const snap = await getDoc(postRef);
      setPost({ id: snap.id, ...snap.data() });
    } catch (e) {
      console.error("비추천 처리 오류:", e);
    }
  };

  // 댓글 작성
  const handleAddComment = async () => {
    if (!myNickname) return alert("로그인 필요");
    if (!commentInput.trim()) return;

    await addDoc(collection(db, "comments"), {
      postId,
      authorNickname: myNickname,
      content: commentInput,
      createdAt: serverTimestamp()
    });

    setCommentInput("");

    const q = query(
      collection(db, "comments"),
      where("postId", "==", postId),
      orderBy("createdAt", "asc")
    );
    const snap = await getDocs(q);
    setComments(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  // 글 삭제
  const handleDelete = async () => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    await updateDoc(doc(db, "posts", postId), { deleted: true });
    navigate("/community");
  };

  if (!post) return <div>로딩중...</div>;
  if (post.deleted) return <div>삭제된 글입니다.</div>;

  return (
    <div style={{ background: "#fafafd", borderRadius: 20, padding: 40, margin: 20 }}>
      <div>
        <h2 style={{ color: "#2563eb" }}>{post.title}</h2>
        <div>{post.authorNickname} | {new Date(post.createdAt?.seconds * 1000).toLocaleDateString()}</div>
        <div style={{ margin: "16px 0" }}>{post.content}</div>
        <div>
          조회수 {post.views || 0} &nbsp;
          추천 {post.likes || 0} / 비추천 {post.dislikes || 0}
        </div>
        {myNickname === post.authorNickname && (
          <button onClick={handleDelete} style={{ background: "#f87171", color: "#fff", borderRadius: 8, margin: 4 }}>글 삭제</button>
        )}
        <button onClick={() => navigate("/community")} style={{ background: "#e5e7eb", borderRadius: 8, margin: 4 }}>목록</button>
      </div>

      <div style={{ margin: "24px 0" }}>
        <button
          onClick={handleLike}
          style={{
            background: post.likedBy?.includes(myNickname) ? "#22c55e" : "#e2e8f0",
            color: post.likedBy?.includes(myNickname) ? "#fff" : "#222",
            fontWeight: 700,
            padding: "10px 24px",
            borderRadius: 8,
            marginRight: 10
          }}
        >
          👍 추천
        </button>
        <button
          onClick={handleDislike}
          style={{
            background: post.dislikedBy?.includes(myNickname) ? "#ef4444" : "#e2e8f0",
            color: post.dislikedBy?.includes(myNickname) ? "#fff" : "#222",
            fontWeight: 700,
            padding: "10px 24px",
            borderRadius: 8
          }}
        >
          👎 비추천
        </button>
      </div>

      <div style={{ background: "#fff", padding: 16, borderRadius: 16 }}>
        <div style={{ color: "#2563eb", fontWeight: 700 }}>댓글</div>
        <div style={{ display: "flex", marginTop: 10 }}>
          <input
            value={commentInput}
            onChange={e => setCommentInput(e.target.value)}
            style={{ flex: 1, marginRight: 8, border: "1px solid #ddd", borderRadius: 8, padding: 8 }}
            placeholder="댓글 입력"
          />
          <button onClick={handleAddComment} style={{ background: "#2563eb", color: "#fff", borderRadius: 8, padding: "0 16px" }}>작성</button>
        </div>
        <div style={{ marginTop: 16 }}>
          {comments.length === 0 && <div style={{ color: "#888" }}>아직 댓글이 없습니다.</div>}
          {comments.map(c => (
            <div key={c.id} style={{ borderBottom: "1px solid #eee", padding: 8 }}>
              <b>{c.authorNickname}</b> {c.content}
              <span style={{ color: "#aaa", marginLeft: 8 }}>{c.createdAt && new Date(c.createdAt.seconds * 1000).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PostDetail;
